import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineRefresh, HiOutlineCheckCircle, HiOutlineXCircle,
  HiOutlineCog, HiOutlineSearch,
  HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineDownload,
} from 'react-icons/hi';
import { getCloudRunJobs, getServiceTags, setServiceTag } from '../services/api';
import ServiceTagDropdown, { groupAndSort, getTagLabel, TagFilter, stickyTh, stickyTd, stickyGroupTd } from '../components/ServiceTagDropdown';
import './CloudScheduler.css';

const SERVICE_TYPE = 'cloud-run-job';

function formatCost(cost) {
  if (cost === null || cost === undefined) return '—';
  if (cost === 0) return <span className="cost-cell"><span className="cost-approx">~</span> ₹0</span>;
  return <span className="cost-cell"><span className="cost-approx">~</span> ₹{cost.toLocaleString('en-IN')}</span>;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function statusBadge(status) {
  if (!status || status === 'Unknown') return <span className="status-badge neutral">{status || 'Unknown'}</span>;
  if (status === 'Succeeded' || status === 'Completed') return <span className="status-badge success"><HiOutlineCheckCircle size={12} /> {status}</span>;
  if (status === 'Failed') return <span className="status-badge error"><HiOutlineXCircle size={12} /> Failed</span>;
  if (status === 'Running') return <span className="status-badge warning">Running</span>;
  return <span className="status-badge neutral">{status}</span>;
}

function escapeCsvField(val) {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function CloudRunJobs({ gcpProjects, selectedProjectId }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [tagMap, setTagMap] = useState({});
  const [tagFilter, setTagFilter] = useState('all');

  const activeProject = gcpProjects.find(p => p._id === selectedProjectId);

  const fetchJobs = useCallback(async (signal) => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await getCloudRunJobs(activeProject._id);
      if (!signal?.cancelled) setJobs(res.data || []);
    } catch (err) {
      if (!signal?.cancelled) {
        toast.error('Failed to load Cloud Run jobs: ' + (err.response?.data?.error || err.message));
        setJobs([]);
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [activeProject]);

  const fetchTags = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await getServiceTags(activeProject._id);
      const map = {};
      for (const [key, val] of Object.entries(res.data || {})) {
        if (key.startsWith(SERVICE_TYPE + ':')) {
          map[key.replace(SERVICE_TYPE + ':', '')] = val;
        }
      }
      setTagMap(map);
    } catch { /* tags are optional */ }
  }, [activeProject]);

  useEffect(() => {
    setJobs([]);
    setExpandedRows({});
    setTagMap({});
    if (!activeProject) return;
    const signal = { cancelled: false };
    fetchJobs(signal);
    fetchTags();
    return () => { signal.cancelled = true; };
  }, [activeProject, fetchJobs, fetchTags]);

  const handleTagChange = async (serviceName, tag) => {
    const prev = { ...tagMap };
    setTagMap(m => tag ? { ...m, [serviceName]: tag } : (() => { const n = { ...m }; delete n[serviceName]; return n; })());
    try {
      await setServiceTag(activeProject._id, SERVICE_TYPE, serviceName, tag);
    } catch {
      setTagMap(prev);
      toast.error('Failed to update tag');
    }
  };

  const sortFn = useCallback((list) => {
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, []);

  const grouped = useMemo(() => {
    let list = jobs;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(j =>
        j.name.toLowerCase().includes(q) ||
        j.region.toLowerCase().includes(q) ||
        (j.lastExecution?.status || '').toLowerCase().includes(q)
      );
    }
    return groupAndSort(list, tagMap, 'name', sortFn, tagFilter);
  }, [jobs, search, tagMap, sortFn, tagFilter]);

  const flatFiltered = useMemo(() => grouped.filter(r => !r._groupHeader), [grouped]);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  const COL_COUNT = 10;

  const downloadCSV = () => {
    if (flatFiltered.length === 0) return;
    const headers = ['Tag', 'Job Name', 'Region', 'Last Status', 'Est. Cost (INR)', 'Executions', 'Last Execution', 'Started', 'Completed'];
    const rows = flatFiltered.map(j => [
      escapeCsvField(getTagLabel(tagMap[j.name]) || ''),
      escapeCsvField(j.name),
      escapeCsvField(j.region),
      escapeCsvField(j.lastExecution?.status || ''),
      escapeCsvField(''),
      escapeCsvField(j.executionCount ?? ''),
      escapeCsvField(j.lastExecution?.name || ''),
      escapeCsvField(j.lastExecution?.createTime || ''),
      escapeCsvField(j.lastExecution?.completionTime || ''),
    ]);
    const csvContent = [
      headers.map(escapeCsvField).join(','),
      ...rows.map(r => r.join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cloud-run-jobs-${activeProject?.projectId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeProject) {
    return (
      <div className="cloud-scheduler page-container">
        <div className="gcp-page-header">
          <div className="gcp-page-title-row"><h1><HiOutlineCog size={28} /> Cloud Run Jobs</h1></div>
        </div>
        <div className="empty-state"><p>Select a GCP project to view Cloud Run Jobs.</p></div>
      </div>
    );
  }

  return (
    <div className="cloud-scheduler page-container">
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineCog size={28} /> Cloud Run Jobs</h1>
          <span className="pv-subtitle">{activeProject.projectName} — {flatFiltered.length} job{flatFiltered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="gcp-page-toolbar">
          <div className="header-search">
            <HiOutlineSearch size={14} />
            <input type="text" placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <TagFilter value={tagFilter} onChange={setTagFilter} />
          <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={loading || flatFiltered.length === 0} title="Download CSV">
            <HiOutlineDownload size={14} /> CSV
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchJobs({})} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && jobs.length === 0 && (
        <div className="empty-state"><p>No Cloud Run Jobs found in <strong>{activeProject.projectName}</strong>.</p></div>
      )}

      {grouped.length > 0 && (
        <div className="resource-table-wrap">
          <div className="resource-table-scroll">
          <table className="resource-table">
            <thead>
              <tr>
                <th style={stickyTh[0]}></th>
                <th style={stickyTh[1]}>Tag</th>
                <th style={stickyTh[2]}>Job Name</th>
                <th>Region</th>
                <th>Last Status</th>
                <th>Est. Cost</th>
                <th>Executions</th>
                <th>Last Execution</th>
                <th>Started</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((row, i) => {
                if (row._groupHeader) {
                  return (
                    <tr key={`gh-${row.group}`} className="group-header-row">
                      <td style={stickyGroupTd[0]}></td>
                      <td style={stickyGroupTd[1]}></td>
                      <td style={stickyGroupTd[2]}>
                        <span className="group-label">
                          <span className={`group-dot ${row.group}`}></span>
                          {row.label}
                          <span className="group-count">({row.count})</span>
                        </span>
                      </td>
                      <td colSpan={COL_COUNT - 3}></td>
                    </tr>
                  );
                }
                const j = row;
                const key = j.fullName || i;
                const isOpen = expandedRows[key];
                return (
                  <React.Fragment key={key}>
                    <tr className="expandable-row" onClick={() => toggleRow(key)}>
                      <td className="expand-toggle" style={stickyTd[0]}>
                        {isOpen ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
                      </td>
                      <td style={stickyTd[1]}>
                        <ServiceTagDropdown value={tagMap[j.name] || ''} onChange={(tag) => handleTagChange(j.name, tag)} />
                      </td>
                      <td className="cell-name" style={stickyTd[2]}>{j.name}</td>
                      <td><span className="region-badge">{j.region}</span></td>
                      <td>{statusBadge(j.lastExecution?.status)}</td>
                      <td>{formatCost(null)}</td>
                      <td>{j.executionCount}</td>
                      <td>{j.lastExecution?.name || '—'}</td>
                      <td>{formatTimestamp(j.lastExecution?.createTime)}</td>
                      <td>{formatTimestamp(j.lastExecution?.completionTime)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="expand-detail-row">
                        <td colSpan={COL_COUNT}>
                          <div className="expand-detail">
                            <div className="detail-grid">
                              <div className="detail-item full-width">
                                <span className="detail-label">Full Name</span>
                                <span className="detail-value mono">{j.fullName || '—'}</span>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Region</span>
                                  <span className="detail-value">{j.region}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Launch Stage</span>
                                  <span className="detail-value">{j.launchStage || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Reconciling</span>
                                  <span className="detail-value">{j.reconciling ? 'Yes' : 'No'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Total Executions</span>
                                  <span className="detail-value">{j.executionCount}</span>
                                </div>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Created</span>
                                  <span className="detail-value">{formatTimestamp(j.createTime)}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Updated</span>
                                  <span className="detail-value">{formatTimestamp(j.updateTime)}</span>
                                </div>
                              </div>
                              {j.lastExecution && (
                                <>
                                  <div className="detail-divider">Last Execution</div>
                                  <div className="detail-row">
                                    <div className="detail-item">
                                      <span className="detail-label">Name</span>
                                      <span className="detail-value mono">{j.lastExecution.name}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Status</span>
                                      <span className="detail-value">{statusBadge(j.lastExecution.status)}</span>
                                    </div>
                                  </div>
                                  <div className="detail-row">
                                    <div className="detail-item">
                                      <span className="detail-label">Succeeded Tasks</span>
                                      <span className="detail-value">{j.lastExecution.succeededCount ?? '—'}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Failed Tasks</span>
                                      <span className="detail-value">{j.lastExecution.failedCount ?? '—'}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Running Tasks</span>
                                      <span className="detail-value">{j.lastExecution.runningCount ?? '—'}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="detail-label">Total Tasks</span>
                                      <span className="detail-value">{j.lastExecution.taskCount ?? '—'}</span>
                                    </div>
                                  </div>
                                </>
                              )}
                              {j.conditions?.length > 0 && (
                                <>
                                  <div className="detail-divider">Job Conditions</div>
                                  <div className="detail-row">
                                    {j.conditions.map((c, ci) => (
                                      <div className="detail-item" key={ci}>
                                        <span className="detail-label">{c.type}</span>
                                        <span className="detail-value">{c.state}{c.message ? ` — ${c.message}` : ''}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!loading && jobs.length > 0 && flatFiltered.length === 0 && (
        <div className="empty-state"><p>No jobs match "<strong>{search}</strong>"</p></div>
      )}
    </div>
  );
}

export default CloudRunJobs;
